// The Notebook's networking and state (#1869). Page → Hook → Component: the
// dialog and its cells fetch nothing; the chat page calls this hook and hands
// its result down.
//
// Cells are derived from the chat's plugin events on every render. The only
// stored part is NotebookState (notes, unsent edits, Confirm/Undo decisions),
// one row per chat behind GET/PUT /agent/:customer/session/:id/notebook with an
// optimistic `version`. A table re-run goes through the browser's own table
// route under the viewer's token: no agent turn and no AI credits, and the
// result only changes what that cell shows (it is never persisted).
import { useEffect, useRef, useState, type MutableRefObject } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../../../constants/system'
import type { AgentComposerHandle, AgentSSEEvent } from '../../../agent'
import type { PlatinumData } from '../../../types/carbon/models/PlatinumData'
import { deriveNotebookCells } from './notebookCells'
import { buildChangesMessage, buildWebappMessage, specAxes } from './notebookMessages'
import {
  EMPTY_NOTEBOOK_STATE,
  type NotebookCell,
  type NotebookDecision,
  type NotebookEdit,
  type NotebookNote,
  type NotebookState,
} from './notebookTypes'

/** How long typing in a cell waits before the edit is saved. */
export const NOTEBOOK_EDIT_SAVE_MS = 600

export const NOTEBOOK_CONFLICT_MESSAGE = 'Someone changed this notebook; your change was not saved.'

export interface NotebookRun {
  data: PlatinumData
  seq: number
  /** The spec that ran, as JSON: the table's caption and its own re-fetches read it. */
  spec: string
}

export interface UseNotebookArgs {
  customer: string | undefined
  sessionId: string | undefined
  /** The viewer may change the notebook: the chat's owner or a Manager. */
  canEdit: boolean
  pluginEvents: AgentSSEEvent[]
  composerHandleRef?: MutableRefObject<AgentComposerHandle | null>
}

export interface NotebookController {
  open: boolean
  /** The cell to bring into view when the dialog opens. */
  focusCellId?: string
  cells: NotebookCell[]
  state: NotebookState
  loading: boolean
  loadError: string
  saveError: string
  canEdit: boolean
  /** The page has a composer to put messages in. */
  hasComposer: boolean
  ticked: string[]
  /** The newest re-run of a table or chart cell, kept for this visit only. `seq` counts runs, so a new result redraws the table. */
  runs: Record<string, NotebookRun>
  running: Record<string, boolean>
  runErrors: Record<string, string>
  handleOpen: (cellId?: string) => void
  handleClose: () => void
  handleAddNote: (after: string | null, text: string) => void
  handleEditNote: (id: string, text: string) => void
  handleDeleteNote: (id: string) => void
  handleDecide: (eventId: string, decision: NotebookDecision) => void
  handleEdit: (cellId: string, edit: NotebookEdit) => void
  handleClearEdit: (cellId: string) => void
  handleRunTable: (cellId: string) => Promise<void>
  handleToggleTick: (cellId: string) => void
  handleSendChanges: () => void
  handleBuildWebapp: () => void
}

function sentence(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: unknown } } })?.response?.data
  return typeof data?.error === 'string' && data.error ? data.error : fallback
}

const statusOf = (err: unknown) => (err as { response?: { status?: number } })?.response?.status

/** The stored row's state, with every field present. */
function fromServer(body: { version?: unknown; state?: unknown } | undefined): NotebookState {
  const s = (body?.state ?? {}) as Partial<NotebookState>
  return {
    version: typeof body?.version === 'number' ? body.version : 0,
    notes: Array.isArray(s.notes) ? s.notes : [],
    decisions: s.decisions && typeof s.decisions === 'object' ? s.decisions : {},
    edits: s.edits && typeof s.edits === 'object' ? s.edits : {},
  }
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

/** The edit with every field that matches the cell's own content left out. */
function effectiveEdit(cell: NotebookCell | undefined, edit: NotebookEdit): NotebookEdit {
  if (!cell) return edit
  const out: NotebookEdit = {}
  if (cell.kind === 'query') {
    if (edit.sql !== undefined && edit.sql !== str(cell.data.sql)) out.sql = edit.sql
    return out
  }
  const original = specAxes({ ...cell, edit: undefined })
  for (const key of ['side', 'top', 'filter'] as const) {
    if (edit[key] !== undefined && edit[key] !== original[key]) out[key] = edit[key]
  }
  return out
}

let noteSeq = 0
const newNoteId = () => `note-${Date.now().toString(36)}-${(noteSeq++).toString(36)}`

export function useNotebook({ customer, sessionId, canEdit, pluginEvents, composerHandleRef }: UseNotebookArgs): NotebookController {
  const [open, setOpen] = useState(false)
  const [focusCellId, setFocusCellId] = useState<string | undefined>()
  const [state, setStateValue] = useState<NotebookState>(EMPTY_NOTEBOOK_STATE)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [ticked, setTicked] = useState<string[]>([])
  const [runs, setRuns] = useState<Record<string, NotebookRun>>({})
  const [running, setRunning] = useState<Record<string, boolean>>({})
  const [runErrors, setRunErrors] = useState<Record<string, string>>({})

  // The local state is the truth between saves; a save sends whatever is
  // newest when it starts, and a change made while one is in flight is sent
  // by a second save straight after.
  const stateRef = useRef(state)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const saving = useRef(false)
  const dirty = useRef(false)
  // Bumped when the page moves to another chat: an answer for the old chat
  // must not land in the new one's state.
  const generation = useRef(0)

  const setState = (next: NotebookState) => {
    stateRef.current = next
    setStateValue(next)
  }

  const cells = deriveNotebookCells(pluginEvents ?? [], state)
  const url = `${API_BASE_URL}/agent/${encodeURIComponent(customer ?? '')}/session/${encodeURIComponent(sessionId ?? '')}/notebook`

  const load = async () => {
    if (!sessionId || !customer) return
    const gen = generation.current
    setLoading(true)
    setLoadError('')
    try {
      const res = await axios.get(url)
      if (gen === generation.current) setState(fromServer(res.data))
    } catch (err) {
      if (gen === generation.current) setLoadError(sentence(err, 'The notebook could not be loaded.'))
    } finally {
      if (gen === generation.current) setLoading(false)
    }
  }

  const flush = async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current)
      saveTimer.current = null
    }
    if (saving.current) {
      dirty.current = true
      return
    }
    const gen = generation.current
    saving.current = true
    try {
      do {
        dirty.current = false
        const { version, notes, decisions, edits } = stateRef.current
        try {
          const res = await axios.put(url, { version, state: { notes, decisions, edits } })
          if (gen !== generation.current) return
          setState({ ...stateRef.current, version: fromServer(res.data).version })
        } catch (err) {
          if (gen !== generation.current) return
          if (statusOf(err) === 409) {
            dirty.current = false
            await load()
            setSaveError(NOTEBOOK_CONFLICT_MESSAGE)
          } else {
            setSaveError(sentence(err, 'The notebook could not be saved.'))
          }
        }
      } while (dirty.current && gen === generation.current)
    } finally {
      if (gen === generation.current) saving.current = false
    }
  }

  // The page keeps this hook from one chat to the next. On leaving a chat, an
  // edit still waiting for its save goes to that chat, and the next chat
  // starts empty.
  useEffect(() => () => {
    if (saveTimer.current) void flush()
    generation.current += 1
    saving.current = false
    dirty.current = false
    stateRef.current = EMPTY_NOTEBOOK_STATE
    setStateValue(EMPTY_NOTEBOOK_STATE)
    setOpen(false)
    setFocusCellId(undefined)
    setTicked([])
    setRuns({})
    setRunErrors({})
    setSaveError('')
    setLoadError('')
  }, [sessionId])

  const change = (next: NotebookState, { debounce = false } = {}) => {
    if (!canEdit) return
    setState(next)
    setSaveError('')
    if (saveTimer.current) clearTimeout(saveTimer.current)
    if (debounce) {
      saveTimer.current = setTimeout(() => void flush(), NOTEBOOK_EDIT_SAVE_MS)
    } else {
      saveTimer.current = null
      void flush()
    }
  }

  const handleOpen = (cellId?: string) => {
    setFocusCellId(cellId)
    setOpen(true)
    // Re-read only when nothing of ours is waiting to be saved.
    if (!saving.current && !saveTimer.current) void load()
  }

  const handleClose = () => {
    setOpen(false)
    setFocusCellId(undefined)
    if (saveTimer.current) void flush()
  }

  const handleAddNote = (after: string | null, text: string) => {
    const note: NotebookNote = { id: newNoteId(), text, after, created_at: Date.now() }
    change({ ...stateRef.current, notes: [...stateRef.current.notes, note] })
  }

  const handleEditNote = (id: string, text: string) => {
    change(
      { ...stateRef.current, notes: stateRef.current.notes.map((n) => (n.id === id ? { ...n, text } : n)) },
      { debounce: true },
    )
  }

  const handleDeleteNote = (id: string) => {
    const s = stateRef.current
    const gone = s.notes.find((n) => n.id === id)
    // Notes that hung off this one move up to where it was.
    const notes = s.notes
      .filter((n) => n.id !== id)
      .map((n) => (n.after === id ? { ...n, after: gone?.after ?? null } : n))
    change({ ...s, notes })
  }

  const handleDecide = (eventId: string, decision: NotebookDecision) => {
    const s = stateRef.current
    const edits = { ...s.edits }
    // A confirmed revision is the agent's answer to the person's edit of that cell.
    if (decision === 'confirmed') {
      const owner = cells.find((c) => c.revisions.some((r) => r.eventId === eventId))
      if (owner) delete edits[owner.id]
    }
    change({ ...s, decisions: { ...s.decisions, [eventId]: decision }, edits })
  }

  const handleEdit = (cellId: string, edit: NotebookEdit) => {
    const s = stateRef.current
    const merged = effectiveEdit(cells.find((c) => c.id === cellId), { ...s.edits[cellId], ...edit })
    const edits = { ...s.edits }
    if (Object.keys(merged).length > 0) edits[cellId] = merged
    else delete edits[cellId]
    change({ ...s, edits }, { debounce: true })
  }

  const handleClearEdit = (cellId: string) => {
    const edits = { ...stateRef.current.edits }
    delete edits[cellId]
    change({ ...stateRef.current, edits })
    setRuns(({ [cellId]: _dropped, ...rest }) => rest)
  }

  const handleRunTable = async (cellId: string) => {
    if (!canEdit) return
    const cell = cells.find((c) => c.id === cellId)
    if (!cell || (cell.kind !== 'table' && cell.kind !== 'chart')) return
    const job = str(cell.data.job)
    const tableCustomer = str(cell.data.customer) || customer
    if (!job || !tableCustomer) {
      setRunErrors((r) => ({ ...r, [cellId]: 'This table does not say which job it ran on, so it cannot be run from here.' }))
      return
    }
    let spec: Record<string, unknown> = {}
    try {
      spec = JSON.parse(str(cell.data.spec) || '{}') as Record<string, unknown>
    } catch {
      // Run with the axes alone.
    }
    const axes = specAxes(cell)
    const body: Record<string, unknown> = { ...spec, side: axes.side, top: axes.top }
    if (axes.filter) body.filter = axes.filter
    else delete body.filter
    // Carbon applies the filter only with useFilter on; without it the table comes back unfiltered.
    body.useFilter = !!axes.filter

    setRunning((r) => ({ ...r, [cellId]: true }))
    setRunErrors((r) => ({ ...r, [cellId]: '' }))
    try {
      const res = await axios.post<PlatinumData>(
        `${API_BASE_URL}/tables/${encodeURIComponent(tableCustomer)}/${encodeURIComponent(job)}/run?format=json&frequency=false&column_percent=true`,
        body,
      )
      setRuns((r) => ({ ...r, [cellId]: { data: res.data, seq: (r[cellId]?.seq ?? 0) + 1, spec: JSON.stringify(body) } }))
    } catch (err) {
      setRunErrors((r) => ({ ...r, [cellId]: sentence(err, 'The table could not be run.') }))
    } finally {
      setRunning((r) => ({ ...r, [cellId]: false }))
    }
  }

  const handleToggleTick = (cellId: string) => {
    if (!canEdit) return
    setTicked((t) => (t.includes(cellId) ? t.filter((id) => id !== cellId) : [...t, cellId]))
  }

  // Into the composer, after any unsent text, for the person to send.
  const toComposer = (message: string) => {
    const handle = composerHandleRef?.current
    if (!handle || !message) return false
    const current = handle.getInput().trim()
    handle.setInput(current ? `${current}\n\n${message}` : message)
    return true
  }

  const handleSendChanges = () => {
    if (!canEdit) return
    if (toComposer(buildChangesMessage(cells))) handleClose()
  }

  const handleBuildWebapp = () => {
    if (!canEdit || ticked.length === 0) return
    if (toComposer(buildWebappMessage(cells, ticked))) {
      setTicked([])
      handleClose()
    }
  }

  return {
    open,
    focusCellId,
    cells,
    state,
    loading,
    loadError,
    saveError,
    canEdit,
    hasComposer: !!composerHandleRef,
    ticked,
    runs,
    running,
    runErrors,
    handleOpen,
    handleClose,
    handleAddNote,
    handleEditNote,
    handleDeleteNote,
    handleDecide,
    handleEdit,
    handleClearEdit,
    handleRunTable,
    handleToggleTick,
    handleSendChanges,
    handleBuildWebapp,
  }
}

export default useNotebook
